import { ProcedureCamEntry, UserIdToProcedureCamDataDict } from './../../../../../../libs/prism-shared/src/lib/models/procedure-cam-data.model';
import { Injectable } from "@angular/core";
import { AppwriteApiEndpoint, AppwriteApiRoute, Collection, FactoryAsset, MappingAssetToProcedure, MappingAssetToResource, Procedure, ProcedureInstance, ProcedureInstanceStatus, ProcedureInstanceStep, ProcedureStepRedline, ProcedureType, ProcedureVersion, ProcedureVersionStatus, ProcedureLinkSummary, Resource, ResourceStatus, ScheduledProcedure, FreightCarrier, FactoryAssetType, GlobalVariableDocumentIds, EnvironmentId, LaserProgramFilePath, CribbageGame, Customer, CustomerAddress, Vendor, PurchaseRequestList, PurchaseRequest, PurchaseRequestChangeLog, Contact, SoftwareRequest, SmartJeffProject, CommonProject, CommonProjectUpdate, AssetHistoryEvent, MaintenanceTask, PurchaseOrder, BatchSetEntityGroups, BatchDeleteEntityGroups, EmailGroup, DirectChat, DirectChatMessage, FinanceEntry, FinanceEntryType, OvertimeSummaryWeek, normalizeOvertimeWeek, VacationEvent, Employee, EmployeeRecord, EmployeeChangeContext, employeeChangeRecords, UserComment, UpsShipment, UpsShipperAccount, buildUpsShipmentRequest, upsShipmentToInput, parseUpsPackageResults, findUpsResponseValue, parseUpsRateBreakdowns, emptyUpsRateBreakdown, UpsShipmentStatus, UpsPackageStatus, CapitalizationProject, byId, resolveUpsParty, upsShipmentDocIds, UpsShipmentParty, PhoneNumberType, MasterProcedure, MasterProcedureVersion, MasterProcedureStep, FactoryMoveRequest, FactoryMoveRequestStatus, PrismCommonSubModule, PrismModule, User, UserNotification, applyFactoryMoveRequestTransition, compareOpenFactoryMoveRequests, factoryMoveRequestCompletionRecipientIds, toFactoryMoveRequestEditableUpdate, validateEmailRegex } from '@prism/prism-shared';
import { Query, TablesDB } from 'appwrite';
import { environment } from "apps/prism-client/src/environments/environment";
import { catchError, combineLatest, defaultIfEmpty, filter, forkJoin, from, map, Observable, of, shareReplay, switchMap, tap, throwError } from "rxjs";
import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { writeDeviceDocumentReadsToLocalStorage } from "../functions/write-device-document-reads-to-local-storage.function";
import { AppwriteDatabaseService, fromAppwriteDocs } from '@prism/angular-shared';
import { CustomerNoteVersion } from '@prism/prism-shared';
import { AppwriteFunctionsService } from './appwrite-functions.service';
import { SharedApiService } from './shared-api.service';

interface LegacyCarrier {
    id: string;
    name: string;
    imageUrl: string;
}

type ProcedureLinkSummarySource = Pick<ProcedureVersion, 'id' | 'procedureId' | 'name' | 'versionNumber'>;

@Injectable({ providedIn: 'root' })
export class PrismCommonApiService {

    constructor(
        private appwrite: AppwriteDatabaseService,
        private functions: AppwriteFunctionsService,
        private http: HttpClient,
        private sharedApi: SharedApiService,
    ) { }

    // Cached UPS shipper accounts (numbers come from server env vars). Fetched once per session —
    // they essentially never change — and reused across every edit-page open.
    private upsAccounts$?: Observable<UpsShipperAccount[]>;

    public createId(): string {
        return this.appwrite.createId();
    }

    // --- Factory move requests -------------------------------------------------

    public loadFactoryMoveRequest(requestId: string): Observable<FactoryMoveRequest> {
        return this.appwrite.get<FactoryMoveRequest>(Collection.factoryMoveRequest, requestId);
    }

    public loadOpenFactoryMoveRequests(): Observable<FactoryMoveRequest[]> {
        return this.loadFactoryMoveRequests(FactoryMoveRequestStatus.Open).pipe(
            map(response => [...response.items].sort(compareOpenFactoryMoveRequests)),
        );
    }

    /**
     * Loads a complete status lane so browse-page search and department filters
     * apply to the whole result set rather than only the current 100-row page.
     */
    public loadFactoryMoveRequests(status: FactoryMoveRequestStatus): Observable<{ items: FactoryMoveRequest[]; total: number }> {
        const dateField = status === FactoryMoveRequestStatus.Completed
            ? 'completedOn'
            : status === FactoryMoveRequestStatus.Canceled
                ? 'canceledOn'
                : 'deadline';
        const order = status === FactoryMoveRequestStatus.Open
            ? Query.orderAsc(dateField)
            : Query.orderDesc(dateField);
        return this.appwrite.listAll<FactoryMoveRequest>(Collection.factoryMoveRequest, [
            Query.equal('status', status),
            order,
        ]).pipe(
            map(items => ({ items, total: items.length })),
        );
    }

    public loadFactoryMoveRequestPage(
        status: FactoryMoveRequestStatus,
        offset = 0,
        limit = 100,
    ): Observable<{ items: FactoryMoveRequest[]; total: number }> {
        const dateField = status === FactoryMoveRequestStatus.Completed
            ? 'completedOn'
            : status === FactoryMoveRequestStatus.Canceled
                ? 'canceledOn'
                : 'deadline';
        const order = status === FactoryMoveRequestStatus.Open
            ? Query.orderAsc(dateField)
            : Query.orderDesc(dateField);
        return this.appwrite.list<FactoryMoveRequest>(Collection.factoryMoveRequest, [
            Query.equal('status', status),
            order,
            Query.offset(offset),
            Query.limit(limit),
        ]);
    }

    /** Watches move requests that contain any of the supplied source order-line ids. */
    public watchFactoryMoveRequestsForOrderLineIds(orderLineIds: string[]): Observable<FactoryMoveRequest[]> {
        const uniqueIds = [...new Set(orderLineIds.filter(Boolean))];
        if (!uniqueIds.length) return of([]);

        const batches: string[][] = [];
        for (let index = 0; index < uniqueIds.length; index += 30) {
            batches.push(uniqueIds.slice(index, index + 30));
        }

        return combineLatest(batches.map((ids) => this.appwrite.collection<FactoryMoveRequest>(
            Collection.factoryMoveRequest,
            (query) => query.where('linkedOrderLineIds', 'array-contains-any', ids).limit(5000),
        ).valueChanges())).pipe(
            map((groups) => [...new Map(groups.flat().map((request) => [request.id, request] as const)).values()]),
        );
    }

    public createFactoryMoveRequest(
        request: FactoryMoveRequest,
        actor: User,
        users: User[],
    ): Observable<void> {
        const notifications: UserNotification[] = users
            .filter(user => user.id !== actor.id
                && !!user.abilities?.[PrismModule.PrismCommon]?.[PrismCommonSubModule.MoveRequests]?.ReceivesMoveRequestNotifications)
            .map(user => this.factoryMoveRequestNotification(
                user.id,
                `${actor.firstName} created move request: ${request.title}`,
                request.id,
            ));

        return this.appwrite.batchSetAcrossCollectionsTransactional({
            [Collection.factoryMoveRequest]: [request],
            [Collection.userNotification]: notifications,
        });
    }

    public updateFactoryMoveRequest(request: FactoryMoveRequest): Observable<void> {
        return this.loadFactoryMoveRequest(request.id).pipe(
            switchMap(current => {
                if (current.status !== FactoryMoveRequestStatus.Open) {
                    return throwError(() => new Error(`Move request is already ${current.status.toLowerCase()}. Refresh before editing.`));
                }
                return from(this.appwrite.collection<FactoryMoveRequest>(Collection.factoryMoveRequest).doc(request.id).update(
                    toFactoryMoveRequestEditableUpdate(request),
                ));
            }),
        );
    }

    public completeFactoryMoveRequest(requestId: string, actor: User): Observable<void> {
        return this.transitionFactoryMoveRequest(requestId, FactoryMoveRequestStatus.Completed, actor);
    }

    public cancelFactoryMoveRequest(requestId: string, actor: User): Observable<void> {
        return this.transitionFactoryMoveRequest(requestId, FactoryMoveRequestStatus.Canceled, actor);
    }

    private transitionFactoryMoveRequest(
        requestId: string,
        status: FactoryMoveRequestStatus.Completed | FactoryMoveRequestStatus.Canceled,
        actor: User,
    ): Observable<void> {
        return this.loadFactoryMoveRequest(requestId).pipe(
            switchMap(current => {
                if (current.status !== FactoryMoveRequestStatus.Open) {
                    return throwError(() => new Error(`Move request is already ${current.status.toLowerCase()}.`));
                }

                const updated = applyFactoryMoveRequestTransition(current, status, actor.id);
                const recipientIds = status === FactoryMoveRequestStatus.Completed
                    ? factoryMoveRequestCompletionRecipientIds(current, actor.id)
                    : current.createdById && current.createdById !== actor.id
                        ? [current.createdById]
                        : [];
                const notifications = recipientIds.map(userId => this.factoryMoveRequestNotification(
                    userId,
                    `${actor.firstName} ${status === FactoryMoveRequestStatus.Completed ? 'completed' : 'canceled'} move request: ${current.title}`,
                    current.id,
                ));

                return this.appwrite.batchSetAcrossCollectionsTransactional({
                    [Collection.factoryMoveRequest]: [updated],
                    [Collection.userNotification]: notifications,
                });
            }),
        );
    }

    private factoryMoveRequestNotification(userId: string, description: string, requestId: string): UserNotification {
        return {
            id: this.appwrite.createId(),
            userId,
            description,
            createdOn: new Date(),
            acknowledgedOn: null,
            associatedUrl: `${PrismModule.PrismCommon}/${PrismCommonSubModule.MoveRequests}/view/${requestId}`,
        };
    }

    public loadUpsAccounts(): Observable<UpsShipperAccount[]> {
        if (!this.upsAccounts$) {
            const isLocal = environment.environmentId === EnvironmentId.LocalTesting
                || environment.environmentId === EnvironmentId.LocalStaging
                || environment.environmentId === EnvironmentId.LocalProduction;
            const path = `/ups/${AppwriteApiEndpoint.UpsGetAccounts}`;
            const source = isLocal
                ? this.http.get<{ accounts: UpsShipperAccount[] }>(`http://localhost:3333${path}`)
                : this.functions.get<{ accounts: UpsShipperAccount[] }>('api', path);
            this.upsAccounts$ = source.pipe(
                map(res => res?.accounts ?? []),
                catchError(() => of([] as UpsShipperAccount[])),
                shareReplay(1),
            );
        }
        return this.upsAccounts$;
    }

    // Limit 1000 — we don't paginate yet. Originals preserved as a /* */ block
    // at the bottom of this section for revert.
    public loadResources(): Observable<Resource[]> {
        return this.appwrite.list<Resource>(Collection.resource, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }
    public loadCribbageGames(): Observable<CribbageGame[]> {
        return this.appwrite.list<CribbageGame>(Collection.cribbageGame, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public setCribbageGame(game: CribbageGame): Observable<void> {
        return from(this.appwrite.collection<CribbageGame>(Collection.cribbageGame).doc(game.id).set(game));
    }
    public setResource(resource: Resource): Observable<void> {
        return from(this.appwrite.collection<Resource>(Collection.resource).doc(resource.id).set(resource));
    }

    public loadResourceById(resourceId: string): Observable<Resource> {
        return this.appwrite.get<Resource>(Collection.resource, resourceId);
    }

    public loadResourcesByIds(resourceIds: string[]): Observable<Resource[]> {
        return this.appwrite.listByIds<Resource>(Collection.resource, resourceIds);
    }

    public getResourceCount(): Observable<number> {
        return this.appwrite.list<Resource>(Collection.resource, [Query.limit(1)]).pipe(
            map(resp => resp.total)
        );
    }

    public loadActiveResources(): Observable<Resource[]> {
        return this.appwrite.list<Resource>(Collection.resource, [
            Query.equal('status', ResourceStatus.ACTIVE),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadProcedures(): Observable<Procedure[]> {
        return this.appwrite.list<Procedure>(Collection.procedure, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadProceduresAndHighestReleasedVersion(): Observable<{
        procedures: Procedure[];
        procedureVersions: ProcedureVersion[];
    }> {
        return this.loadProcedures().pipe(
            switchMap((procedures) => {
                const versions$ = procedures.map((procedure) => {
                    return this.loadReleasedProcedureVersionByProcedureId(procedure.id)
                })
                return forkJoin(versions$).pipe(
                    defaultIfEmpty([]),
                    map((procedureVersions) => ({
                        procedures,
                        procedureVersions
                    }))
                )
            })
        )
    }

    public loadReleasedProcedureVersions(): Observable<ProcedureVersion[]> {
        return this.appwrite.list<ProcedureVersion>(Collection.procedureVersion, [
            Query.equal('status', ProcedureVersionStatus.Released),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadReleasedProcedureLinkSummaries(): Observable<ProcedureLinkSummary[]> {
        return this.appwrite.listAll<ProcedureLinkSummarySource>(Collection.procedureVersion, [
            Query.equal('status', ProcedureVersionStatus.Released),
            Query.select(['$id', 'procedureId', 'name', 'versionNumber']),
        ]).pipe(
            map((versions) => {
                const latestVersionByProcedureId = new Map<string, ProcedureLinkSummarySource>();
                for (const version of versions) {
                    const existing = latestVersionByProcedureId.get(version.procedureId);
                    if (!existing || (version.versionNumber ?? 0) > (existing.versionNumber ?? 0)) {
                        latestVersionByProcedureId.set(version.procedureId, version);
                    }
                }

                return [...latestVersionByProcedureId.values()].map((version) => ({
                    procedureId: version.procedureId,
                    procedureVersionId: version.id,
                    name: version.name,
                    versionNumber: version.versionNumber,
                }));
            })
        );
    }

    public loadProcedureLinkSummary(procedureId: string): Observable<ProcedureLinkSummary | null> {
        return this.appwrite.listAll<ProcedureLinkSummarySource>(Collection.procedureVersion, [
            Query.equal('procedureId', procedureId),
            Query.equal('status', ProcedureVersionStatus.Released),
            Query.select(['$id', 'procedureId', 'name', 'versionNumber']),
        ]).pipe(
            map((versions) => {
                const latestVersionByProcedureId = new Map<string, ProcedureLinkSummarySource>();
                for (const version of versions) {
                    const existing = latestVersionByProcedureId.get(version.procedureId);
                    if (!existing || (version.versionNumber ?? 0) > (existing.versionNumber ?? 0)) {
                        latestVersionByProcedureId.set(version.procedureId, version);
                    }
                }

                return [...latestVersionByProcedureId.values()].map((version) => ({
                    procedureId: version.procedureId,
                    procedureVersionId: version.id,
                    name: version.name,
                    versionNumber: version.versionNumber,
                }))[0] || null;
            })
        );
    }

    public loadProcedureLinkSummariesByProcedureIds(procedureIds: string[]): Observable<ProcedureLinkSummary[]> {
        return this.appwrite.listByFieldValues<ProcedureLinkSummarySource>(
            Collection.procedureVersion,
            'procedureId',
            procedureIds,
            [
                Query.equal('status', ProcedureVersionStatus.Released),
                Query.select(['$id', 'procedureId', 'name', 'versionNumber']),
            ],
        ).pipe(
            map((versions) => {
                const latestVersionByProcedureId = new Map<string, ProcedureLinkSummarySource>();
                for (const version of versions) {
                    const existing = latestVersionByProcedureId.get(version.procedureId);
                    if (!existing || (version.versionNumber ?? 0) > (existing.versionNumber ?? 0)) {
                        latestVersionByProcedureId.set(version.procedureId, version);
                    }
                }

                return [...latestVersionByProcedureId.values()].map((version) => ({
                    procedureId: version.procedureId,
                    procedureVersionId: version.id,
                    name: version.name,
                    versionNumber: version.versionNumber,
                }));
            })
        );
    }

    public loadMasterProcedures(): Observable<MasterProcedure[]> {
        return this.appwrite.list<MasterProcedure>(Collection.masterProcedure, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMasterProcedure(masterProcedureId: string): Observable<MasterProcedure> {
        return this.appwrite.get<MasterProcedure>(Collection.masterProcedure, masterProcedureId);
    }

    public loadMasterProcedureVersions(): Observable<MasterProcedureVersion[]> {
        return this.appwrite.list<MasterProcedureVersion>(Collection.masterProcedureVersion, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMasterProcedureVersion(masterProcedureVersionId: string): Observable<MasterProcedureVersion> {
        return this.appwrite.get<MasterProcedureVersion>(Collection.masterProcedureVersion, masterProcedureVersionId);
    }

    public loadMasterProcedureVersionsByMasterProcedureId(masterProcedureId: string): Observable<MasterProcedureVersion[]> {
        return this.appwrite.list<MasterProcedureVersion>(Collection.masterProcedureVersion, [
            Query.equal('masterProcedureId', masterProcedureId),
            Query.limit(1000),
        ]).pipe(map(resp => resp.items));
    }

    public loadMasterProcedureData(masterProcedureId: string, masterProcedureVersionId: string): Observable<{
        masterProcedure: MasterProcedure;
        versions: MasterProcedureVersion[];
        selectedVersion: MasterProcedureVersion;
        steps: MasterProcedureStep[];
    }> {
        return forkJoin({
            masterProcedure: this.loadMasterProcedure(masterProcedureId),
            versions: this.loadMasterProcedureVersionsByMasterProcedureId(masterProcedureId),
            selectedVersion: this.loadMasterProcedureVersion(masterProcedureVersionId),
        }).pipe(map(data => ({ ...data, steps: data.selectedVersion.steps })));
    }

    public getNextMasterProcedureCode(): Observable<string> {
        return this.loadMasterProcedures().pipe(
            map(procedures => procedures.reduce((max, procedure) => {
                const match = /^MP\.(\d+)$/.exec(procedure.code);
                return Math.max(max, match ? Number(match[1]) : 0);
            }, 0) + 1),
            map(next => `MP.${next.toString().padStart(3, '0')}`),
        );
    }

    public createMasterProcedure(masterProcedure: MasterProcedure, version: MasterProcedureVersion): Observable<void> {
        return this.appwrite.batchSetAcrossCollectionsTransactional({
            [Collection.masterProcedure]: [masterProcedure],
            [Collection.masterProcedureVersion]: [version],
        });
    }

    public saveMasterProcedureDraft(version: MasterProcedureVersion): Observable<void> {
        return this.appwrite.batchSetAcrossCollectionsTransactional({
            [Collection.masterProcedureVersion]: [version],
        });
    }

    public createMasterProcedureRevision(version: MasterProcedureVersion): Observable<void> {
        return this.appwrite.batchSetAcrossCollectionsTransactional({
            [Collection.masterProcedureVersion]: [version],
        });
    }

    public releaseMasterProcedureVersion(
        draft: MasterProcedureVersion,
        userId: string,
        releaseComment: string,
    ): Observable<MasterProcedureVersion> {
        return this.loadMasterProcedureVersionsByMasterProcedureId(draft.masterProcedureId).pipe(
            switchMap(versions => {
                const persistedDraft = versions.find(version => version.id === draft.id);
                if (!persistedDraft || persistedDraft.status !== ProcedureVersionStatus.Draft) {
                    return throwError(() => new Error('only a current draft master procedure can be released'));
                }
                const nextVersionNumber = versions.reduce((max, version) => Math.max(max, version.versionNumber ?? 0), 0) + 1;
                const releasedVersion: MasterProcedureVersion = {
                    ...draft,
                    status: ProcedureVersionStatus.Released,
                    versionNumber: nextVersionNumber,
                    releasedById: userId,
                    releasedOn: new Date(),
                    releaseComment,
                    updatedById: userId,
                    updatedOn: new Date(),
                };
                const versionsToSave = [
                    ...versions
                        .filter(version => version.status === ProcedureVersionStatus.Released)
                        .map(version => ({ ...version, status: ProcedureVersionStatus.Obsolete })),
                    releasedVersion,
                ];
                return this.appwrite.batchSetAcrossCollectionsTransactional({
                    [Collection.masterProcedureVersion]: versionsToSave,
                }).pipe(map(() => releasedVersion));
            }),
        );
    }

    public deleteMasterProcedureDraft(versionId: string): Observable<void> {
        return this.loadMasterProcedureVersion(versionId).pipe(
            switchMap(version => {
                if (version.status !== ProcedureVersionStatus.Draft) {
                    return throwError(() => new Error('only a draft master procedure can be deleted'));
                }
                return this.loadMasterProcedureVersionsByMasterProcedureId(version.masterProcedureId).pipe(
                    switchMap(versions => this.appwrite.batchSetAndDeleteAcrossCollectionsTransactional(
                        {},
                        {
                            [Collection.masterProcedure]: versions.length === 1 ? [version.masterProcedureId] : [],
                            [Collection.masterProcedureVersion]: [versionId],
                        },
                    )),
                );
            }),
        );
    }

    public loadReleasedPlasticsProcedureVersions(): Observable<ProcedureVersion[]> {
        return this.appwrite.list<Procedure>(Collection.procedure, [
            Query.equal('type', ProcedureType.Plastics),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
            switchMap(procedures => {
                if (!procedures.length) {
                    return of([]);
                }
                const versions$ = procedures.map(p =>
                    this.loadReleasedProcedureVersionByProcedureId(p.id)
                );
                return forkJoin(versions$);
            })
        );
    }

    public loadReleasedProcedureVersionByProcedureId(procedureId: string): Observable<ProcedureVersion> {
        return this.appwrite.list<ProcedureVersion>(Collection.procedureVersion, [
            Query.equal('procedureId', procedureId),
            Query.equal('status', ProcedureVersionStatus.Released),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.items[0]),
        );
    }

    public loadReleasedProcedureVersionsByProcedureIds(procedureIds: string[]): Observable<ProcedureVersion[]> {
        return this.appwrite.listByFieldValues<ProcedureVersion>(
            Collection.procedureVersion,
            'procedureId',
            procedureIds,
            [Query.equal('status', ProcedureVersionStatus.Released)],
        );
    }

    public loadCompletedProcedureInstancesWithLimit(limit: number): Observable<ProcedureInstance[]> {
        return this.appwrite.list<ProcedureInstance>(Collection.procedureInstance, [
            Query.orderDesc('completedOn'),
            Query.limit(Math.max(limit * 2, limit)),
        ]).pipe(
            map((resp) => resp.items
                .filter((instance) => instance.status === ProcedureInstanceStatus.Completed)
                .slice(0, limit)),
            catchError((err) => {
                console.error(err);
                return of([])
            })
        );
    }

    public loadProcedureInstancesByStatuses(statuses: ProcedureInstanceStatus[]): Observable<ProcedureInstance[]> {
        return this.appwrite.list<ProcedureInstance>(Collection.procedureInstance, [
            Query.equal('status', statuses),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadProcedureInstancesByAssetId(assetId: string): Observable<ProcedureInstance[]> {
        return this.appwrite.list<ProcedureInstance>(Collection.procedureInstance, [
            Query.equal('assetId', assetId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadProcedureVersion(versionId: string): Observable<ProcedureVersion> {
        return this.appwrite.get<ProcedureVersion>(Collection.procedureVersion, versionId);
    }

    public loadProcedureVersions(): Observable<ProcedureVersion[]> {
        return this.appwrite.list<ProcedureVersion>(Collection.procedureVersion, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadProcedureVersionsByIds(procedureVersionIds: string[]): Observable<ProcedureVersion[]> {
        return this.appwrite.listByIds<ProcedureVersion>(Collection.procedureVersion, procedureVersionIds);
    }

    public loadProcedureAndHighestProcedureVersion(procedureId: string): Observable<{
        procedure: Procedure;
        procedureVersion: ProcedureVersion;
    }> {
        return forkJoin([
            this.loadProcedure(procedureId),
            this.loadReleasedProcedureVersionByProcedureId(procedureId)
        ]).pipe(
            map(([procedure, procedureVersion]) => ({ procedure, procedureVersion }))
        );
    }

    public loadProceduresAndHighestProcedureVersionsByProcedureIds(procedureIds: string[]): Observable<{
        procedures: Procedure[];
        procedureVersions: ProcedureVersion[];
    }> {
        return forkJoin({
            procedures: this.loadProceduresByIds(procedureIds),
            procedureVersions: this.loadReleasedProcedureVersionsByProcedureIds(procedureIds),
        });
    }

    public loadProcedureInstances(): Observable<ProcedureInstance[]> {
        return this.appwrite.list<ProcedureInstance>(Collection.procedureInstance, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public setProcedureInstance(instance: ProcedureInstance): Observable<void> {
        return from(this.appwrite.collection<ProcedureInstance>(Collection.procedureInstance).doc(instance.id).set(instance));
    }

    public setProcedureInstanceSteps(steps: ProcedureInstanceStep[]): Observable<void[]> {
        const updates$ = steps.map((step) => from(this.appwrite.collection<ProcedureInstanceStep>(Collection.procedureInstanceStep).doc(step.id).set(step)));
        return forkJoin(updates$).pipe(
            defaultIfEmpty([])
        );
    }

    public loadProcedureInstanceStepsByProcedureInstanceId(instanceId: string): Observable<ProcedureInstanceStep[]> {
        return this.appwrite.list<ProcedureInstanceStep>(Collection.procedureInstanceStep, [
            Query.equal('instanceId', instanceId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public getProcedureInstanceStepsByProcedureInstanceIdValueChanges(instanceId: string) {
        return this.appwrite.collection<ProcedureInstanceStep>(
            Collection.procedureInstanceStep,
            (ref) => ref.where('instanceId', '==', instanceId),
        ).valueChanges();
    }

    public getProcedureInstanceByIdValueChanges(instanceId: string) {
        return this.appwrite.collection<ProcedureInstance>(Collection.procedureInstance).doc(instanceId).valueChanges().pipe(
            filter((instance): instance is ProcedureInstance => !!instance),
        );
    }


    public loadProcedureAndVersionsByProcedureId(procedureId: string): Observable<{
        procedure: Procedure,
        procedureVersions: ProcedureVersion[]
    }> {
        return forkJoin([
            this.appwrite.get<Procedure>(Collection.procedure, procedureId),
            this.appwrite.list<ProcedureVersion>(Collection.procedureVersion, [
                Query.equal('procedureId', procedureId),
                Query.limit(1000),
            ]).pipe(map((resp) => resp.items))
        ]).pipe(
            defaultIfEmpty([]),
            map(([procedure, procedureVersions]) => ({ procedureVersions, procedure }))
        );
    }

    public setProcedureVersion(version: ProcedureVersion): Observable<void> {
        return from(this.appwrite.collection<ProcedureVersion>(Collection.procedureVersion).doc(version.id).set(version));
    }

    public setProcedure(procedure: Procedure): Observable<void> {
        return from(this.appwrite.collection<Procedure>(Collection.procedure).doc(procedure.id).set(procedure));
    }

    public loadProcedure(procedureId: string): Observable<Procedure> {
        return this.appwrite.get<Procedure>(Collection.procedure, procedureId);
    }

    public loadProcedureStepRedlinesByProcedureId(procedureId: string): Observable<ProcedureStepRedline[]> {
        return this.appwrite.list<ProcedureStepRedline>(Collection.procedureStepRedline, [
            Query.equal('procedureId', procedureId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public deleteProcedureVersion(procedureId: string): Observable<void> {
        return this.appwrite.delete(Collection.procedureVersion, procedureId);
    }

    public getReleasedProcedureVersionCount(procedureId: string) {
        return this.appwrite.list<ProcedureVersion>(Collection.procedureVersion, [
            Query.equal('procedureId', procedureId),
            Query.equal('status', [ProcedureVersionStatus.Released, ProcedureVersionStatus.Obsolete]),
            Query.limit(1),
        ]).pipe(map((resp) => resp.total));
    }

    public setCurrentReleasedProcedureVersionToObsolete(procedureId: string) {
        return this.appwrite.list<ProcedureVersion>(Collection.procedureVersion, [
            Query.equal('procedureId', procedureId),
            Query.equal('status', ProcedureVersionStatus.Released),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items),
            switchMap((versions) => {
                const updates$ = versions.map(v => from(this.appwrite.collection<ProcedureVersion>(Collection.procedureVersion).doc(v.id).set({
                    ...v,
                    status: ProcedureVersionStatus.Obsolete,
                })))
                return forkJoin(updates$).pipe(
                    defaultIfEmpty([])
                )
            })
        );
    }

    public setProcedureStepRedline(redline: ProcedureStepRedline): Observable<void> {
        return from(this.appwrite.collection<ProcedureStepRedline>(Collection.procedureStepRedline).doc(redline.id).set(redline));
    }

    public loadProcedureStepRedlinesByCreatedProcedureVersionId(procedureVersionId: string): Observable<ProcedureStepRedline[]> {
        return this.appwrite.list<ProcedureStepRedline>(Collection.procedureStepRedline, [
            Query.equal('createdProcedureVersionId', procedureVersionId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadProcedureById(procedureId: string): Observable<Procedure> {
        return this.appwrite.get<Procedure>(Collection.procedure, procedureId);
    }

    public loadProceduresByIds(procedureIds: string[]): Observable<Procedure[]> {
        return this.appwrite.listByIds<Procedure>(Collection.procedure, procedureIds);
    }

    public loadProcedureVersionById(versionId: string): Observable<ProcedureVersion> {
        return this.appwrite.get<ProcedureVersion>(Collection.procedureVersion, versionId);
    }

    public getProcedureVersionByIdValueChanges(versionId: string): Observable<ProcedureVersion> {
        return this.appwrite.collection<ProcedureVersion>(Collection.procedureVersion).doc(versionId).valueChanges().pipe(
            map((version) => {
                if (!version) return null as unknown as ProcedureVersion;
                return version as ProcedureVersion;
            })
        );
    }

    public setScheduledProcedure(scheduledProcedure: ScheduledProcedure): Observable<void> {
        return from(this.appwrite.collection<ScheduledProcedure>(Collection.scheduledProcedure).doc(scheduledProcedure.id).set(scheduledProcedure));
    }

    public deleteScheduledProcedure(scheduledProcedure: ScheduledProcedure): Observable<void> {
        return this.appwrite.delete(Collection.scheduledProcedure, scheduledProcedure.id);
    }

    public loadScheduledProcedures(): Observable<ScheduledProcedure[]> {
        return this.appwrite.list<ScheduledProcedure>(Collection.scheduledProcedure, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadScheduledProceduresByProcedureId(procedureId: string): Observable<ScheduledProcedure[]> {
        return this.appwrite.list<ScheduledProcedure>(Collection.scheduledProcedure, [
            Query.equal('procedureId', procedureId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items),
        );
    }

    public loadAssetHistoryEventsByAssetId(assetId: string): Observable<AssetHistoryEvent[]> {
        return this.appwrite.list<AssetHistoryEvent>(Collection.assetHistoryEvent, [
            Query.equal('assetId', assetId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items),
        );
    }

    public setAssetHistoryEvent(event: AssetHistoryEvent): Observable<void> {
        return from(this.appwrite.collection<AssetHistoryEvent>(Collection.assetHistoryEvent).doc(event.id).set(event));
    }


    public loadScheeduledProceduresByAssetId(assetId: string): Observable<ScheduledProcedure[]> {
        return this.appwrite.list<ScheduledProcedure>(Collection.scheduledProcedure, [
            Query.equal('assetId', assetId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }


    public setFactoryAsset(factoryAsset: FactoryAsset): Observable<void> {
        return from(this.appwrite.collection<FactoryAsset>(Collection.factoryAsset).doc(factoryAsset.id).set(factoryAsset));
    }

    public loadFactoryAssets(): Observable<FactoryAsset[]> {
        return this.appwrite.list<FactoryAsset>(Collection.factoryAsset, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadFactoryAssetById(assetId: string): Observable<FactoryAsset> {
        return this.appwrite.get<FactoryAsset>(Collection.factoryAsset, assetId);
    }

    public loadFactoryAssetsByType(assetType: FactoryAssetType): Observable<FactoryAsset[]> {
        return this.appwrite.list<FactoryAsset>(Collection.factoryAsset, [
            Query.equal('type', assetType),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items)
        );
    }

    public loadFactoryAssetsByIds(assetIds: string[]): Observable<FactoryAsset[]> {
        return this.appwrite.listByIds<FactoryAsset>(Collection.factoryAsset, assetIds);
    }

    public loadResourcesByFactoryAssetId(assetId: string): Observable<Resource[]> {
        return this.appwrite.list<MappingAssetToResource>(Collection.mappingAssetToResource, [
            Query.equal('assetId', assetId),
            Query.limit(1000),
        ]).pipe(
            switchMap((resp) => {
                const resourceIds = resp.items.map((doc) => doc.resourceId as string)

                return this.loadResourcesByIds(resourceIds);
            })
        )

    }

    loadFactoryAssetsByResourceId(resourceId: string): Observable<FactoryAsset[]> {
        return this.appwrite.list<MappingAssetToResource>(Collection.mappingAssetToResource, [
            Query.equal('resourceId', resourceId),
            Query.limit(1000),
        ]).pipe(
            switchMap((resp) => {
                const factoryAssetIds = resp.items.map((doc) => doc.assetId as string)
                return this.loadFactoryAssetsByIds(factoryAssetIds);
            })
        )
    }


    public loadMappingAssetToResources(): Observable<MappingAssetToResource[]> {
        return this.appwrite.list<MappingAssetToResource>(Collection.mappingAssetToResource, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadMappingAssetToResourcesByAssetId(assetId: string): Observable<MappingAssetToResource[]> {
        return this.appwrite.list<MappingAssetToResource>(Collection.mappingAssetToResource, [
            Query.equal('assetId', assetId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public getMappingAssetToResourceByAssetAndResourceId(assetId: string, resourceId: string): Observable<MappingAssetToResource> {
        return this.appwrite.list<MappingAssetToResource>(Collection.mappingAssetToResource, [
            Query.equal('assetId', assetId),
            Query.equal('resourceId', resourceId),
            Query.limit(1),
        ]).pipe(
            map((resp) => resp.items[0])
        );
    }

    public setMappingAssetToResource(mapping: MappingAssetToResource): Observable<void> {
        return from(this.appwrite.collection<MappingAssetToResource>(Collection.mappingAssetToResource).doc(mapping.id).set(mapping));
    }

    public deleteMappingAssetToResource(mapping: MappingAssetToResource): Observable<void> {
        return this.appwrite.delete(Collection.mappingAssetToResource, mapping.id);
    }

    loadReleasedProcedureVersionsByFactoryAssetId(assetId: string): Observable<{ procedureVersions: ProcedureVersion[], procedures: Procedure[] }> {
        return this.appwrite.list<MappingAssetToProcedure>(Collection.mappingAssetToProcedure, [
            Query.equal('assetId', assetId),
            Query.limit(1000),
        ]).pipe(
            switchMap((resp) => {
                const procedureIds = resp.items.map((doc) => doc.procedureId as string)

                const procedureVersions$: Observable<ProcedureVersion[]> = this.loadReleasedProcedureVersionsByProcedureIds(procedureIds);
                const procedures$: Observable<Procedure[]> = this.loadProceduresByIds(procedureIds);

                return combineLatest([
                    procedureVersions$,
                    procedures$,
                ]).pipe(
                    map(([procedureVersions, procedures]) => {
                        return {
                            procedureVersions,
                            procedures
                        }
                    })
                )
            })
        )
    }

    loadMappingAssetToProcedureByAssetId(assetId: string): Observable<MappingAssetToProcedure[]> {
        return this.appwrite.list<MappingAssetToProcedure>(Collection.mappingAssetToProcedure, [
            Query.equal('assetId', assetId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    setMappingAssetToProcedure(mapping: MappingAssetToProcedure): Observable<void> {
        return from(this.appwrite.collection<MappingAssetToProcedure>(Collection.mappingAssetToProcedure).doc(mapping.id).set(mapping));
    }

    deleteMappingAssetToProcedure(mapping: MappingAssetToProcedure): Observable<void> {
        return this.appwrite.delete(Collection.mappingAssetToProcedure, mapping.id);
    }

    getMappingAssetToProcedureByAssetAndProcedureId(assetId: string, procedureId: string): Observable<MappingAssetToProcedure> {
        return this.appwrite.list<MappingAssetToProcedure>(Collection.mappingAssetToProcedure, [
            Query.equal('assetId', assetId),
            Query.equal('procedureId', procedureId),
            Query.limit(1),
        ]).pipe(
            map((resp) => resp.items[0])
        );
    }

    public getProcedureCountByType(type: ProcedureType): Observable<number> {
        return this.appwrite.list<Procedure>(Collection.procedure, [
            Query.equal('type', type),
            Query.limit(1),
        ]).pipe(
            map((resp) => resp.total)
        );
    }

    public getProcedureInstanceCount(): Observable<number> {
        return this.appwrite.list<ProcedureInstance>(Collection.procedureInstance, [Query.limit(1)]).pipe(
            map((resp) => resp.total)
        );
    }

    public getInProgressProcedureInstanceValueChanges(): Observable<ProcedureInstance[]> {
        return this.appwrite.collection<ProcedureInstance>(
            Collection.procedureInstance,
            ref => ref
                .where('status', 'in', [ProcedureInstanceStatus.InProgress, ProcedureInstanceStatus.NotStarted])
        ).valueChanges().pipe(
            map((instances) => instances.sort((a, b) => a.createdOn.getTime() - b.createdOn.getTime()))
        )
    }

    public setFreightCarrier(freightCarrier: FreightCarrier): Observable<void> {
        return from(this.appwrite.collection<FreightCarrier>(Collection.freightCarrier).doc(freightCarrier.id).set(freightCarrier));
    }

    public setFreightCarriers(freightCarriers: FreightCarrier[]): Observable<void> {
        return from(this.appwrite.batchSet(freightCarriers, Collection.freightCarrier))
    }

    public loadLegacyCarriers(): Observable<LegacyCarrier[]> {
        return this.appwrite.list<LegacyCarrier>('trucking-carrier', [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        )
    }

    public loadFreightCarriers(): Observable<FreightCarrier[]> {
        return this.appwrite.list<FreightCarrier>(Collection.freightCarrier, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadCapitalizationProjects(): Observable<CapitalizationProject[]> {
        return this.appwrite.list<CapitalizationProject>(Collection.capitalizationProject, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public setCapitalizationProject(project: CapitalizationProject): Observable<void> {
        return from(this.appwrite.collection<CapitalizationProject>(Collection.capitalizationProject).doc(project.id).set(project));
    }

    public setLaserProgramFilePaths(): Observable<void> {

        let laserProgramFilePaths: LaserProgramFilePath[] = [];

        if ((
            environment.environmentId === EnvironmentId.Staging
        ) || (
                environment.environmentId === EnvironmentId.LocalStaging
            )) {
            laserProgramFilePaths = [
                {
                    laserAssetId: 'lpqvpyWE9d8NOFqMJB0T',
                    programFilePath: 'C:\\laser-programming\\small-laser-program',
                },
                {
                    laserAssetId: 'S8uynUbouMYWXODdE3jd',
                    programFilePath: 'C:\\laser-programming\\big-laser-program',
                },
            ]
        }

        if ((
            environment.environmentId === EnvironmentId.Production
        ) || (
                environment.environmentId === EnvironmentId.LocalProduction
            )) {
            laserProgramFilePaths = [
                {
                    laserAssetId: 'xt7JbaukF6u7KUyjaScy',
                    programFilePath: 'C:\\laser-programming\\small-laser-program',
                },
                {
                    laserAssetId: 'rZFNnZlwzTyJVeizn75a',
                    programFilePath: 'C:\\laser-programming\\big-laser-program',
                },
            ]
        }

        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.LaserProgramFilePaths).set({
            laserProgramFilePaths
        }));
    }

    public loadLaserProgramFilePaths(): Observable<LaserProgramFilePath[]> {
        return this.appwrite.get<{ laserProgramFilePaths: LaserProgramFilePath[] }>(
            Collection.globalVariables,
            GlobalVariableDocumentIds.LaserProgramFilePaths,
        ).pipe(
            map(resp => resp?.laserProgramFilePaths as LaserProgramFilePath[])
        )
    }

    public loadProductDrawingResourceById(resourceId: string): Observable<Resource> {
        return this.appwrite.get<Resource>(Collection.resource, resourceId);
    }

    public loadCustomers(): Observable<Customer[]> {
        return this.appwrite.list<Customer>(Collection.customer, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadCustomerById(customerId: string): Observable<Customer> {
        return this.appwrite.get<Customer>(Collection.customer, customerId);
    }

    public loadCustomersByIds(customerIds: string[]): Observable<Customer[]> {
        return this.appwrite.listByIds<Customer>(Collection.customer, customerIds);
    }

    public setCustomer(customer: Customer): Observable<void> {
        return from(this.appwrite.collection<Customer>(Collection.customer).doc(customer.id).set(customer));
    }

    public loadCustomerNoteVersion(customerId: string, versionId: string): Observable<CustomerNoteVersion | null> {
        if (!versionId) return of(null);
        return this.appwrite.get<CustomerNoteVersion>(Collection.customerNoteVersion, versionId).pipe(
            map(version => {
                if (version.customerId !== customerId) throw new Error('The selected note belongs to another customer.');
                return version;
            }),
        );
    }

    public saveCustomerNoteVersion(
        customerId: string,
        expectedVersionId: string,
        plain: string,
        createdById: string,
    ): Observable<CustomerNoteVersion> {
        return from(this.saveCustomerNoteVersionTransaction(customerId, expectedVersionId, plain, createdById));
    }

    /** Only this command writes the customer's current-note pointer. */
    private async saveCustomerNoteVersionTransaction(
        customerId: string,
        expectedVersionId: string,
        plain: string,
        createdById: string,
    ): Promise<CustomerNoteVersion> {
        if (!customerId || !createdById || typeof plain !== 'string') throw new Error('Customer, author, and note text are required.');
        const tables = new TablesDB(this.appwrite.client);
        const databaseId = this.appwrite.databaseId;
        const customer = await tables.getRow({ databaseId, tableId: Collection.customer, rowId: customerId });
        if ((customer['notesCurrentVersionId'] || '') !== expectedVersionId) {
            throw new Error('These notes changed since you opened them. Reload the customer and try again.');
        }
        const version: CustomerNoteVersion = {
            id: this.appwrite.createId(), customerId, plain,
            createdOn: new Date(), createdById,
        };
        const transaction = await tables.createTransaction({ ttl: 60 });
        try {
            await tables.createRow({
                databaseId, tableId: Collection.customerNoteVersion, rowId: version.id,
                data: {
                    customerId: version.customerId, plain: version.plain,
                    createdOn: version.createdOn.toISOString(), createdById: version.createdById,
                },
                permissions: [], transactionId: transaction.$id,
            });
            await tables.updateRow({
                databaseId, tableId: Collection.customer, rowId: customerId,
                data: {
                    notesCurrentVersionId: version.id,
                    lastSavedOn: version.createdOn.toISOString(), lastSavedById: createdById,
                },
                transactionId: transaction.$id,
            });
            const fresh = await tables.getRow({ databaseId, tableId: Collection.customer, rowId: customerId });
            if (fresh.$updatedAt !== customer.$updatedAt) {
                throw new Error('This customer changed while the notes were saving. Reload and try again.');
            }
            await tables.updateTransaction({ transactionId: transaction.$id, commit: true });
            return version;
        } catch (error) {
            try { await tables.updateTransaction({ transactionId: transaction.$id, rollback: true }); }
            catch { console.warn('Could not roll back customer note transaction.'); }
            throw error;
        }
    }

    // --- customer addresses (own collection: customer-address) ---

    // A customer's address book — only addresses still linked to the customer. Orphaned
    // (deleted) addresses have customerId '' and are excluded. Indexed by customerId.
    public loadAddressesByCustomerId(customerId: string): Observable<CustomerAddress[]> {
        return this.appwrite.list<CustomerAddress>(Collection.customerAddress, [
            Query.equal('customerId', customerId),
            Query.limit(1000),
        ]).pipe(map(resp => resp.items));
    }

    public loadAddressById(id: string): Observable<CustomerAddress> {
        return this.appwrite.get<CustomerAddress>(Collection.customerAddress, id);
    }

    // Resolve referenced address docs directly by id (orders/shipments hold ID-only
    // references that may point at orphaned or other-customer docs — these won't be in
    // any customer's book, so loadAddressesByCustomerId can't find them).
    public loadAddressesByIds(ids: string[]): Observable<CustomerAddress[]> {
        return this.appwrite.listByIds<CustomerAddress>(Collection.customerAddress, ids);
    }

    // Ad-hoc address queries (e.g. Query.equal('region','TX')) — the whole point of the
    // collection. Pass any Appwrite queries; a high limit is appended.
    public loadAddresses(queries: string[] = []): Observable<CustomerAddress[]> {
        return this.appwrite.list<CustomerAddress>(Collection.customerAddress, [...queries, Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public setAddress(address: CustomerAddress): Observable<void> {
        return from(this.appwrite.collection<CustomerAddress>(Collection.customerAddress).doc(address.id).set(address));
    }

    // "Delete" an address: unlink it from the customer (keep the doc for old shipments).
    public unlinkAddress(address: CustomerAddress): Observable<void> {
        return this.setAddress({ ...address, customerId: '' });
    }

    // --- UPS shipments (shared across departments) ---

    public loadUpsShipments(): Observable<UpsShipment[]> {
        return this.appwrite.list<UpsShipment>(Collection.upsShipment, [Query.limit(5000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadUpsShipmentById(id: string): Observable<UpsShipment> {
        return this.appwrite.get<UpsShipment>(Collection.upsShipment, id);
    }

    // Void (cancel) a shipment at UPS by its tracking / ShipmentIdentificationNumber. Relays the
    // server route, which DELETEs to UPS (CIE in non-production). The caller flips the stored status.
    public voidUpsShipment(trackingNumber: string): Observable<{ baseUrl?: string; data?: unknown }> {
        const isLocal = environment.environmentId === EnvironmentId.LocalTesting
            || environment.environmentId === EnvironmentId.LocalStaging
            || environment.environmentId === EnvironmentId.LocalProduction;
        const path = `/ups/${AppwriteApiEndpoint.UpsVoidShipment}`;
        const body = { trackingNumber };
        return isLocal
            ? this.http.post<{ baseUrl?: string; data?: unknown }>(`http://localhost:3333${path}`, body)
            : this.functions.post<{ baseUrl?: string; data?: unknown }>('api', path, body);
    }

    // TEMP: the CIE test shipments carry placeholder tracking numbers UPS can't void, so in
    // non-production voidUpsShipmentVerified skips the UPS call and treats the void as succeeded.
    // Hard-guarded so it can NEVER take effect in production — real voids always hit UPS there.
    // Remove once CIE has valid labels.
    private readonly bypassUpsVoid =
        environment.environmentId !== EnvironmentId.Production &&
        environment.environmentId !== EnvironmentId.LocalProduction;

    /**
     * Void a shipment's label at UPS and VERIFY UPS accepted it (success = void response code '1').
     * Emits once on success; errors with UPS's message otherwise, so callers can gate follow-up
     * writes (status flips, clones, releases) on a confirmed void. UPS voids the whole shipment by
     * its ShipmentIdentificationNumber; falls back to the lead package's tracking number.
     */
    public voidUpsShipmentVerified(shipment: UpsShipment): Observable<void> {
        // TEMP bypass (non-production): see bypassUpsVoid above.
        if (this.bypassUpsVoid) return of(void 0);
        const tracking = shipment.shipmentIdentificationNumber
            || (shipment.packages ?? []).find(p => p.trackingNumber)?.trackingNumber || '';
        if (!tracking) return throwError(() => new Error('No tracking number to void.'));
        return this.voidUpsShipment(tracking).pipe(
            map(res => {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const data: any = res?.data ?? res;
                const code = data?.VoidShipmentResponse?.Response?.ResponseStatus?.Code
                    ?? data?.VoidShipmentResponse?.SummaryResult?.Status?.Code;
                if (code !== '1') throw new Error(this.extractUpsError(res) || 'Void failed');
                return void 0;
            }),
            catchError((err: unknown) => err instanceof Error
                ? throwError(() => err)
                : throwError(() => new Error(this.extractUpsError(err) || 'Void failed'))),
        );
    }

    // Refresh a shipment's tracking from UPS and persist it server-side — the same derivation the
    // tracking cron runs, for one shipment on demand (the view pages call this on open). The server
    // skips Draft/Delivered/Voided and recently-checked shipments; when `changed`, re-load the
    // shipment to pick up the new statuses.
    public refreshUpsShipmentTracking(shipmentId: string): Observable<{ changed?: boolean; skipped?: boolean; throttled?: boolean; status?: string }> {
        const isLocal = environment.environmentId === EnvironmentId.LocalTesting
            || environment.environmentId === EnvironmentId.LocalStaging
            || environment.environmentId === EnvironmentId.LocalProduction;
        const path = `/ups/${AppwriteApiEndpoint.UpsTrackShipment}`;
        const body = { shipmentId };
        return isLocal
            ? this.http.post<{ changed?: boolean; skipped?: boolean; throttled?: boolean; status?: string }>(`http://localhost:3333${path}`, body)
            : this.functions.post<{ changed?: boolean; skipped?: boolean; throttled?: boolean; status?: string }>('api', path, body);
    }

    public setUpsShipment(shipment: UpsShipment): Observable<void> {
        return from(this.appwrite.collection<UpsShipment>(Collection.upsShipment).doc(shipment.id).set(shipment));
    }

    /**
     * Load ONE shipment's referenced address/contact docs and assemble its ship-to / ship-from
     * parties. For a single shipment (process, view page); a LIST resolves in one batch instead —
     * flatten `upsShipmentDocIds` across the list and call `loadAddressesByIds`/`loadContactsByIds`
     * once each, then `resolveUpsParty` per row.
     */
    public resolveUpsParties(shipment: UpsShipment): Observable<ResolvedUpsParties> {
        const { addressIds, contactIds } = upsShipmentDocIds(shipment);
        return forkJoin({
            addresses: addressIds.length ? this.loadAddressesByIds(addressIds) : of([] as CustomerAddress[]),
            contacts: contactIds.length ? this.loadContactsByIds(contactIds) : of([] as Contact[]),
        }).pipe(
            map(({ addresses, contacts }) => {
                const addressMap = byId(addresses);
                const contactMap = byId(contacts);
                // An UNPERSISTED draft's typed party has no doc to resolve yet — its one-time doc
                // is only minted at persist time — but the typed values ride on the draft's
                // in-memory snapshot, so fall back to that (else re-opening the builder before the
                // host saves would present blank fields). Stored shipments never take this branch:
                // their ids resolve, and docs loaded from the DB carry no snapshot.
                const partyFor = (
                    doc: CustomerAddress | undefined, contact: Contact | undefined,
                    residential: boolean, snapshot: UpsShipmentParty | undefined,
                ): UpsShipmentParty =>
                    !doc && ((snapshot?.address?.line1 ?? '').trim() || (snapshot?.address?.city ?? '').trim())
                        ? { ...(snapshot as UpsShipmentParty), residential }
                        : resolveUpsParty(doc, contact, residential);
                return {
                    shipTo: partyFor(
                        addressMap.get(shipment.addressId), contactMap.get(shipment.contactId),
                        shipment.shipToResidential ?? false, shipment.shipTo,
                    ),
                    shipFrom: partyFor(
                        addressMap.get(shipment.shipFromAddressId), contactMap.get(shipment.shipFromContactId),
                        shipment.shipFromResidential ?? false, shipment.shipFrom,
                    ),
                };
            }),
        );
    }

    /**
     * Give a UPS shipment's TYPED (one-time) parties real `CustomerAddress`/`Contact` documents and
     * return the shipment stamped with their ids. The single mint point for both persist paths —
     * the standalone builder's save/process and the plastics edit-shipment save — so a free-typed
     * label ends up as id-resolvable as a book-picked one (which is what lets the snapshots go away,
     * and what lights up edit-shipment's address tags for we-schedule ground).
     *
     * Per party the decision comes from the shipment itself, so no caller has to pass its tab state:
     *   - id set → doc is reusable (book-picked, or the shipper account's default origin): leave alone.
     *   - id set → doc is `isOneTimeUse`: the user re-typed it, so UPDATE that doc in place (never
     *     spawn a second one-time doc for the same party across re-saves).
     *   - id empty + a snapshot with a street line: MINT a fresh one-time doc.
     *   - id empty + no address typed: nothing to mint (e.g. an origin left to the account default).
     *
     * Ownership: the ship-to one-time doc belongs to the shipment's customer (`fallbackCustomerId`
     * covers the drop-ship tab, which clears the customer control); the ship-from one-time doc
     * belongs to its own ship-from customer. Genuinely customer-less parties are orphaned
     * (`customerId: ''`) — one-time docs are hidden from every picker either way.
     *
     * Called at PERSIST time only. The builder rebuilds its draft on every keystroke, so minting on
     * emit would leave an orphan doc per edit.
     */
    public persistUpsOneTimeParties(draft: UpsShipment, userId: string, fallbackCustomerId = ''): Observable<UpsShipment> {
        const trim = (s: string | undefined | null) => (s ?? '').trim();
        const addressIds = [draft.addressId, draft.shipFromAddressId].map(trim).filter(id => !!id);
        const contactIds = [draft.contactId, draft.shipFromContactId].map(trim).filter(id => !!id);

        return forkJoin({
            addresses: addressIds.length ? this.loadAddressesByIds(addressIds) : of([] as CustomerAddress[]),
            contacts: contactIds.length ? this.loadContactsByIds(contactIds) : of([] as Contact[]),
        }).pipe(
            switchMap(({ addresses, contacts }) => {
                const now = new Date();
                const writes: Observable<unknown>[] = [];
                const stamped: UpsShipment = { ...draft };

                // Returns the id the party should carry, queueing the doc write only when the party
                // was TYPED. A SELECTED party (`typed` false) is a pure reference — its id is
                // already on the draft and its doc is left untouched, which is what lets a plastics
                // shipment point at an order's one-time address without rewriting the order's doc.
                const mintParty = (
                    party: UpsShipmentParty | undefined,
                    addressId: string,
                    contactId: string,
                    ownerCustomerId: string,
                    typed: boolean,
                ): { addressId: string; contactId: string } => {
                    if (!typed) return { addressId, contactId };
                    const existingAddress = addresses.find(a => a.id === trim(addressId));
                    // Belt-and-suspenders: a reusable book doc is never ours to rewrite either.
                    if (existingAddress && !existingAddress.isOneTimeUse) return { addressId, contactId };
                    if (!trim(party?.address?.line1)) return { addressId, contactId };

                    const resolvedAddressId = trim(addressId) || this.appwrite.createId();

                    // Contact resolution happens FIRST so the minted address doc can carry its id
                    // as the attached attention contact — the one place the pair is born together.
                    const contactName = trim(party?.contactName);
                    const existingContact = contacts.find(c => c.id === trim(contactId));
                    let resolvedContactId: string;
                    let mintContact = false;
                    if (!contactName) {
                        resolvedContactId = existingContact && !existingContact.isOneTimeUse ? contactId : '';
                    } else if (existingContact && !existingContact.isOneTimeUse) {
                        resolvedContactId = contactId;
                    } else {
                        resolvedContactId = trim(contactId) || this.appwrite.createId();
                        mintContact = true;
                    }

                    writes.push(this.setAddress({
                        id: resolvedAddressId,
                        customerId: ownerCustomerId,
                        label: existingAddress?.label ?? '',
                        address: {
                            line1: party?.address?.line1 ?? '',
                            line2: party?.address?.line2 ?? '',
                            city: party?.address?.city ?? '',
                            region: party?.address?.region ?? '',
                            postalCode: party?.address?.postalCode ?? '',
                            country: party?.address?.country || 'US',
                        },
                        isShippingAddress: true,
                        isBillingAddress: existingAddress?.isBillingAddress ?? false,
                        isDefault: false,
                        isOneTimeUse: true,
                        // UPS calls the destination line "company"; the address book calls it the
                        // receiver — same field, so a minted doc reads correctly in both places.
                        receiverName: trim(party?.company),
                        // One-time docs never carry an attention link — a one-time contact
                        // can't be edited outside its owning flow, so it never becomes an
                        // address default (the shipment itself stores both ids).
                        contactId: '',
                        createdOn: existingAddress?.createdOn ?? now,
                        createdById: existingAddress?.createdById || userId,
                        updatedOn: now,
                        updatedById: userId,
                    }));

                    if (!mintContact) return { addressId: resolvedAddressId, contactId: resolvedContactId };

                    const phone = trim(party?.phone);
                    const email = trim(party?.email);
                    const phoneNumber = phone ? { type: PhoneNumberType.Office, number: phone, extension: null } : null;
                    writes.push(this.setContact({
                        id: resolvedContactId,
                        name: contactName,
                        imageUrl: '', thumbnailImageUrl: '', notes: '', title: '',
                        emails: email ? [email] : [],
                        primaryEmail: email,
                        phoneNumbers: phoneNumber ? [phoneNumber] : [],
                        primaryPhoneNumber: phoneNumber,
                        associatedCustomerIds: ownerCustomerId ? [ownerCustomerId] : [],
                        associatedVendorIds: [],
                        isOneTimeUse: true,
                        createdById: existingContact?.createdById || userId,
                        createdOn: existingContact?.createdOn ?? now,
                    }));
                    return { addressId: resolvedAddressId, contactId: resolvedContactId };
                };

                // Typed vs selected is read straight off the draft: a SELECTED party always carries
                // its owning customer id (the ship-to customer / the ship-from customer or account
                // default), while a TYPED drop-ship orphans it to ''. So an empty owner id === typed.
                const shipToTyped = !trim(draft.customerId);
                const shipFromTyped = !trim(draft.shipFromCustomerId);

                const shipTo = mintParty(draft.shipTo, draft.addressId, draft.contactId, trim(draft.customerId) || trim(fallbackCustomerId), shipToTyped);
                stamped.addressId = shipTo.addressId;
                stamped.contactId = shipTo.contactId;

                const shipFrom = mintParty(draft.shipFrom, draft.shipFromAddressId, draft.shipFromContactId, trim(draft.shipFromCustomerId), shipFromTyped);
                stamped.shipFromAddressId = shipFrom.addressId;
                stamped.shipFromContactId = shipFrom.contactId;

                return writes.length ? forkJoin(writes).pipe(map(() => stamped)) : of(stamped);
            }),
        );
    }

    /**
     * Process a stored UPS Draft at UPS: create the label + tracking number, apply the billed rate,
     * and persist the shipment as Processed — returning the saved entity. The entity-based
     * counterpart to the builder's process()/createShipment(), so a caller without the live builder
     * form (e.g. releasing a Ground plastics shipment) can still submit to UPS. Errors (UPS
     * rejection, or no label returned) propagate so the caller can block on failure.
     */
    public processUpsShipment(draft: UpsShipment, userId: string): Observable<UpsShipment> {
        const isLocal = environment.environmentId === EnvironmentId.LocalTesting
            || environment.environmentId === EnvironmentId.LocalStaging
            || environment.environmentId === EnvironmentId.LocalProduction;
        const path = `/ups/${AppwriteApiEndpoint.UpsCreateShipment}`;
        const post$ = this.resolveUpsParties(draft).pipe(
            switchMap(resolved => {
                // A label is billable and undeliverable if it goes out with a blank destination, so
                // this asserts rather than degrading to the empty address the display layer would
                // happily render. It should be unreachable — both entry points require an address
                // before their process/save button enables, and addresses are soft-unlinked (never
                // hard-deleted), so a referenced doc can't vanish underneath a shipment.
                if (!(resolved.shipTo?.address?.line1 ?? '').trim()) {
                    return throwError(() => new Error('This shipment has no ship-to address — it cannot be sent to UPS.'));
                }
                const body = buildUpsShipmentRequest(upsShipmentToInput(draft, resolved));
                return isLocal
                    ? this.http.post<{ baseUrl?: string; data?: unknown }>(`http://localhost:3333${path}`, body)
                    : this.functions.post<{ baseUrl?: string; data?: unknown }>('api', path, body);
            }),
        );

        return post$.pipe(
            switchMap(res => {
                const data = res?.data ?? res;
                const labels = parseUpsPackageResults(data);
                if (!labels.some(l => l.labelZpl)) {
                    return throwError(() => new Error('UPS did not return a label.'));
                }
                const { published, negotiated } = parseUpsRateBreakdowns(data);
                const money = (amount: string, currency: string): string =>
                    amount ? `${currency === 'USD' || !currency ? '$' : currency + ' '}${amount}` : '';
                // Whole-shipment inquiry number (tracking + void). Prefer UPS's explicit
                // ShipmentIdentificationNumber; fall back to the lead package's tracking number.
                const shipmentIdentificationNumber = findUpsResponseValue(data, 'ShipmentIdentificationNumber')
                    ?? labels.find(l => l.trackingNumber)?.trackingNumber ?? '';
                const now = new Date();
                const processed: UpsShipment = {
                    ...draft,
                    status: UpsShipmentStatus.Processed,
                    shipmentIdentificationNumber,
                    // Results order matches the Package array we sent, so labels[i] ↔ packages[i].
                    packages: (draft.packages ?? []).map((p, i) => {
                        const trackingNumber = labels[i]?.trackingNumber || p.trackingNumber || '';
                        return {
                            ...p,
                            trackingNumber,
                            labelZpl: labels[i]?.labelZpl || p.labelZpl || '',
                            status: trackingNumber ? UpsPackageStatus.LabelCreated : (p.status || ''),
                            exceptionDetail: '',
                        };
                    }),
                    publishedCost: money(published.total, published.currency),
                    negotiatedCost: negotiated ? money(negotiated.total, negotiated.currency) : '',
                    publishedBreakdown: published,
                    negotiatedBreakdown: negotiated ?? emptyUpsRateBreakdown(),
                    processedOn: now,
                    updatedOn: now,
                    updatedById: userId || draft.updatedById,
                };
                // The conversion layer no longer persists the party snapshots (they're resolved
                // from ids), so this stores ids only; the returned object keeps its parties in memory.
                return this.setUpsShipment(processed).pipe(map(() => processed));
            }),
            catchError((err: unknown) => throwError(() => new Error(this.extractUpsError(err) || 'UPS request failed'))),
        );
    }

    // Pull UPS's error text out of a failed create/void response. The server wraps UPS's response as
    // { baseUrl, data }; UPS errors live under data.response.errors. On a 4xx that body rides on
    // Angular's HttpErrorResponse `.error` (local) or the AppwriteFunctionsService error's `.body`
    // (deployed).
    private extractUpsError(err: unknown): string {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const body: any = err instanceof HttpErrorResponse ? err.error : ((err as { body?: unknown })?.body ?? err);
        const data = (body?.data ?? body) as { response?: { errors?: { code?: string; message?: string }[] } } | string | undefined;
        const upsErrors = (typeof data === 'object' && data?.response?.errors) || undefined;
        if (Array.isArray(upsErrors) && upsErrors.length) {
            return 'UPS rejected the request: ' + upsErrors.map(e => `${e.code ? `[${e.code}] ` : ''}${e.message ?? ''}`.trim()).filter(Boolean).join('; ');
        }
        if (typeof body?.error === 'string') return body.error;
        if (typeof data === 'string' && data.trim()) return data;
        return (err as Error)?.message ?? '';
    }

    public loadPlasticsCustomers(): Observable<Customer[]> {
        return this.appwrite.list<Customer>(Collection.customer, [Query.equal('isPlasticsCustomer', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMetalsCustomers(): Observable<Customer[]> {
        return this.appwrite.list<Customer>(Collection.customer, [Query.equal('isMetalsCustomer', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadC3Customers(): Observable<Customer[]> {
        return this.appwrite.list<Customer>(Collection.customer, [Query.equal('isC3Customer', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadInjectionMoldingCustomers(): Observable<Customer[]> {
        return this.appwrite.list<Customer>(Collection.customer, [Query.equal('isInjectionMoldingCustomer', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    // Limit 1000 — we don't paginate yet. ~160 vendors today, fine.
    public loadVendors(): Observable<Vendor[]> {
        return this.appwrite.list<Vendor>(Collection.vendor, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadPlasticsVendors(): Observable<Vendor[]> {
        return this.appwrite.list<Vendor>(Collection.vendor, [Query.equal('isPlasticsVendor', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadMetalsVendors(): Observable<Vendor[]> {
        return this.appwrite.list<Vendor>(Collection.vendor, [Query.equal('isMetalsVendor', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadC3Vendors(): Observable<Vendor[]> {
        return this.appwrite.list<Vendor>(Collection.vendor, [Query.equal('isC3Vendor', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadAutomationVendors(): Observable<Vendor[]> {
        return this.appwrite.list<Vendor>(Collection.vendor, [Query.equal('isAutomationVendor', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadInjectionMoldingVendors(): Observable<Vendor[]> {
        return this.appwrite.list<Vendor>(Collection.vendor, [Query.equal('isInjectionMoldingVendor', true), Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadVendorById(vendorId: string): Observable<Vendor> {
        return this.appwrite.get<Vendor>(Collection.vendor, vendorId);
    }

    public loadVendorsByIds(vendorIds: string[]): Observable<Vendor[]> {
        return this.appwrite.listByIds<Vendor>(Collection.vendor, vendorIds);
    }

    public setVendor(vendor: Vendor): Observable<void> {
        return from(this.appwrite.collection<Vendor>(Collection.vendor).doc(vendor.id).set(vendor));
    }

    public setPurchaseRequestList(list: PurchaseRequestList): Observable<void> {
        return from(this.appwrite.collection<PurchaseRequestList>(Collection.purchaseRequestList).doc(list.id).set(list));
    }

    public loadPurchaseRequestLists(): Observable<PurchaseRequestList[]> {
        return this.appwrite.list<PurchaseRequestList>(Collection.purchaseRequestList, [Query.limit(1000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadPurchaseRequestListById(listId: string): Observable<PurchaseRequestList> {
        return this.appwrite.get<PurchaseRequestList>(Collection.purchaseRequestList, listId);
    }

    public loadPurchaseRequestListsByIds(listIds: string[]): Observable<PurchaseRequestList[]> {
        return this.appwrite.listByIds<PurchaseRequestList>(Collection.purchaseRequestList, listIds);
    }

    public loadPurchaseRequestListsWithFollowerId(userId: string): Observable<PurchaseRequestList[]> {
        return this.appwrite.list<PurchaseRequestList>(Collection.purchaseRequestList, [Query.limit(1000)]).pipe(
            map((resp) => resp.items.filter((list) => list.followerIds?.includes(userId)))
        );
    }

    public setPurchaseRequest(item: PurchaseRequest): Observable<void> {
        return from(this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).doc(item.id).set(item));
    }

    /**
     * Patches only the named fields of a request, leaving everything else on the stored document
     * untouched. This is the write the PURCHASING PLANNER uses for every change it makes —
     * plannedOrderDate, leadTimeBusinessDays, leadTimeSource, budgetExempt.
     *
     * It has to be a partial write, not setPurchaseRequest(). That one is a full-document set
     * called from roughly fourteen places, each holding its own cached copy of the request, and
     * the comment on appendPurchaseRequestComment below documents what that already costs: two
     * browsers editing at once overwrite each other. A planner that wrote whole documents would
     * put a dragged date in the same race, and losing a plan is worse than losing a keystroke
     * because nothing on screen tells you it happened.
     *
     * Safe by construction rather than by convention: Collection.purchaseRequest is in the
     * presence-aware branch of toAppwriteDocument (appwrite-document-conversion.function.ts), so
     * json, array and date fields absent from `changes` are left alone instead of being written
     * back as defaults. Dropping a collection out of that branch would silently turn every partial
     * write here into a document-gutting one.
     */
    public updatePurchaseRequest(requestId: string, changes: Partial<PurchaseRequest>): Observable<void> {
        return from(this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).doc(requestId).update(changes));
    }

    /**
     * The past orders behind the PLANNER'S LEAD-TIME HISTORY, fetched on their own rather than
     * derived from the requests the purchasing page happens to be holding.
     *
     * It has to be its own read. getPurchaseRequestsValueChanges drops anything completed more
     * than five days ago, and a closed purchase order lives almost exclusively on a completed
     * request — in production that left the planner able to see 45 of 2,936 closed POs, so all
     * but a handful of items answered "no closed POs for this item yet" when most of them had
     * years of history.
     *
     * The rows come back SLIM: select() asks only for the columns the history index keys on, which
     * keeps changeLog, comments and files — the bulk of a purchase request — off the wire. They
     * are identity, not display data; nothing renders them.
     *
     * `completedSince` is an outer bound on the query, not the selection rule. The planner keeps
     * only the few most recent orders per item; the window is what stops the query dragging back
     * years of rows it will throw away.
     */
    public loadCompletedPurchaseRequestsForItems(
        hardwareIds: string[],
        partNumbers: string[],
        completedSince: Date,
    ): Observable<PurchaseRequest[]> {
        const shared = [
            Query.select(['$id', 'hardwareId', 'partNumber', 'vendorId', 'purchaseRequestType', 'completedOn']),
            // completedOn is nullable, so the lower bound also does the work of "is completed"
            Query.greaterThanEqual('completedOn', completedSince.toISOString()),
            Query.orderDesc('completedOn'),
        ];
        // A per-batch limit is REQUIRED: listByFieldValues adds none when it isn't given one, and
        // Appwrite then applies its default page of 25 — a silent truncation of the history.
        return forkJoin([
            this.appwrite.listByFieldValues<PurchaseRequest>(
                Collection.purchaseRequest, 'hardwareId', hardwareIds, shared, 30, 1000),
            this.appwrite.listByFieldValues<PurchaseRequest>(
                Collection.purchaseRequest, 'partNumber', partNumbers, shared, 30, 1000),
        ]).pipe(
            map(([byHardware, byPartNumber]) => [...byHardware, ...byPartNumber]),
        );
    }

    /**
     * Purchase orders that were both placed and received — the only ones a lead time can be
     * measured from. Filtered server-side so a request with a still-open PO doesn't drag it back.
     */
    public loadClosedPurchaseOrdersByPurchaseRequestIds(requestIds: string[]): Observable<PurchaseOrder[]> {
        return this.appwrite.listByFieldValues<PurchaseOrder>(
            Collection.purchaseOrder,
            'purchaseRequestId',
            requestIds,
            [Query.isNotNull('orderedOn'), Query.isNotNull('receivedOn')],
            100,
            500,
        );
    }

    /**
     * Appends a comment without clobbering concurrent edits. The purchase-request and purchasing
     * modules both keep a cached copy of the request and previously wrote the whole document back
     * (set), so two browsers commenting/editing at once would overwrite each other and drop
     * comments. This re-reads the request immediately before writing and merges only the comments
     * array via a partial update, so a comment or field change made elsewhere at the same time
     * survives. Returns the merged request so callers can build notifications off the full chain.
     */
    public appendPurchaseRequestComment(requestId: string, comment: UserComment): Observable<PurchaseRequest> {
        return this.loadPurchaseRequestById(requestId).pipe(
            switchMap((fresh) => {
                const comments = [...(fresh.comments ?? []), comment];
                return from(
                    this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).doc(requestId).update({ comments }),
                ).pipe(map(() => ({ ...fresh, comments }) as PurchaseRequest));
            }),
        );
    }

    /**
     * Save an edit made in one of the two purchase-request dialogs.
     *
     * Two things happen here, and both exist because these dialogs stay open for minutes while
     * other people work on the same request:
     *
     *  · THE FIELDS ARE PATCHED, not set. `changes` is built by the dialog itself and holds only
     *    what its form owns, so an edit no longer writes back the values every other field had when
     *    the dialog opened. That is what stops a requester's quantity change reverting the planner's
     *    order-by date, and an agent's price change reverting the quantity.
     *
     *  · THE CHANGE LOG IS APPENDED, not replaced. Both dialogs add an entry describing what they
     *    changed, and both build that entry on top of the log as it was when they opened. Writing
     *    the array whole means whoever saves second silently deletes the other's entry — the exact
     *    failure appendPurchaseRequestComment was written for, one field over. Re-reading here and
     *    appending narrows the window from "as long as the dialog was open" to the round trip.
     *
     * Entries are matched by id, so a retry of the same save cannot double-log. Returns the merged
     * request, which is what callers diff to build their notifications.
     */
    public applyPurchaseRequestEdit(
        requestId: string,
        changes: Partial<PurchaseRequest>,
        appendedChangeLog: PurchaseRequestChangeLog[],
    ): Observable<PurchaseRequest> {
        return this.loadPurchaseRequestById(requestId).pipe(
            switchMap((fresh) => {
                const existing = fresh.changeLog ?? [];
                const seen = new Set(existing.map((entry) => entry.id));
                const appended = appendedChangeLog.filter((entry) => !seen.has(entry.id));
                const changeLog = [...existing, ...appended];
                // an edit that logged nothing leaves the array out entirely rather than rewriting it
                // with what it just read — one less way for a concurrent append to be lost
                const write: Partial<PurchaseRequest> = appended.length
                    ? { ...changes, changeLog } as Partial<PurchaseRequest>
                    : changes;
                return from(
                    this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).doc(requestId).update(write),
                ).pipe(map(() => ({ ...fresh, ...changes, changeLog }) as PurchaseRequest));
            }),
        );
    }

    /**
     * Writes only the derived order/completion status fields. Used after a purchase order changes:
     * these values are recomputed from the (fresh) purchase orders, so writing the whole request
     * would needlessly carry — and could revert — unrelated fields like comments from a stale
     * snapshot (e.g. a request selected for bulk ordering hours earlier). Partial update keeps the
     * write scoped to exactly the two fields it owns.
     */
    public setPurchaseRequestOrderedAndCompletedStatus(requestId: string, ordered: boolean, completedOn: Date | null): Observable<void> {
        return from(this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).doc(requestId).update({ ordered, completedOn }));
    }

    /* ==================== STATUS FLAGS ====================
       Ready and cancel are single facts about a request, not descriptions of the whole document, so
       they are written as such. They used to be `set({ ...item, readiedOn: new Date() })` from
       whatever copy the page was holding — a smaller version of the dialog problem: the copy is
       usually a second old, but the realtime socket backs off up to thirty seconds when it drops,
       and readying a request during that gap would put the whole document back as it stood when the
       connection died — reverting a plan, a price, a comment, with the save reporting success. */

    /** Ready (`readiedOn` set, with who) or unready (both cleared). */
    public setPurchaseRequestReadiedStatus(requestId: string, readiedById: string, readiedOn: Date | null): Observable<void> {
        return from(this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).doc(requestId).update({ readiedById, readiedOn }));
    }

    public setPurchaseRequestsReadied(requestIds: string[], readiedById: string): Observable<void[]> {
        const readiedOn = new Date();
        return forkJoin(requestIds.map((requestId) => this.setPurchaseRequestReadiedStatus(requestId, readiedById, readiedOn))).pipe(
            defaultIfEmpty([]),
        );
    }

    /**
     * Cancel (all three set) or uncancel (all three cleared). `completedOn` moves with the cancel
     * because a canceled request is finished — that pairing is the reason this is one call rather
     * than the caller writing the fields separately and being able to write half of it.
     */
    public setPurchaseRequestCanceledStatus(
        requestId: string,
        canceledById: string,
        canceledOn: Date | null,
        completedOn: Date | null,
    ): Observable<void> {
        return from(
            this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).doc(requestId).update({ canceledById, canceledOn, completedOn }),
        );
    }

    public setPurchaseRequests(items: PurchaseRequest[]): Observable<void[]> {
        return forkJoin(items.map((item) => this.setPurchaseRequest(item))).pipe(
            defaultIfEmpty([])
        )
    }

    public loadPurchaseRequests(): Observable<PurchaseRequest[]> {
        return this.appwrite.list<PurchaseRequest>(Collection.purchaseRequest, [Query.limit(5000)]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadAllPurchaseRequests(): Observable<PurchaseRequest[]> {
        return this.appwrite.listAll<PurchaseRequest>(Collection.purchaseRequest);
    }

    /* The purchasing-planner-entry CRUD lived here — a live stream of the whole collection,
       set/update/delete, and a by-request cleanup for the cancel path. The plan is four fields
       on the purchase request now (plannedOrderDate, leadTimeBusinessDays, leadTimeSource,
       budgetExempt), so there is no second collection to stream, join or garbage-collect: a
       canceled request carries its own plan away with it, and updatePurchaseRequest above is
       the only write the planner needs. */

    public loadPurchaseRequestsByPurchaseRequestListId(listId: string): Observable<PurchaseRequest[]> {
        return this.appwrite.list<PurchaseRequest>(Collection.purchaseRequest, [
            Query.equal('purchaseRequestListId', listId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadPurchaseRequestById(itemId: string): Observable<PurchaseRequest> {
        return this.appwrite.get<PurchaseRequest>(Collection.purchaseRequest, itemId);
    }

    public loadPurchaseRequestsByIds(requestIds: string[]): Observable<PurchaseRequest[]> {
        return this.appwrite.listByIds<PurchaseRequest>(Collection.purchaseRequest, requestIds);
    }

    public loadIncompletePurchaseRequests(): Observable<PurchaseRequest[]> {
        return this.appwrite.list<PurchaseRequest>(Collection.purchaseRequest, [Query.limit(5000)]).pipe(
            map((resp) => resp.items.filter((request) => !(request as any).receivedOn && !request.canceledOn))
        );
    }

    public getOrderedPurchaseRequestsValueChanges(): Observable<PurchaseRequest[]> {
        return this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).valueChanges().pipe(
            map((items) => items.filter((item) => !!(item as any).orderedOn && !(item as any).receivedOn && !item.canceledOn))
        );
    }

    public getOrderedPurchaseOrdersValueChanges(): Observable<PurchaseOrder[]> {
        return this.appwrite.collection<PurchaseOrder>(Collection.purchaseOrder).valueChanges().pipe(
            map((orders) => orders.filter((order) => !!order.orderedOn && !order.receivedOn))
        );
    }

    public loadActiveOrderedPurchaseRequestsByHardwareItemId(hardwareItemId: string): Observable<PurchaseRequest[]> {
        return this.appwrite.list<PurchaseRequest>(Collection.purchaseRequest, [
            Query.equal('hardwareId', hardwareItemId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items.filter((request) => request.ordered && !request.completedOn && !request.canceledOn))
        );
    }

    /**
     * Hard-deletes one purchase order.
     *
     * Callers MUST follow this with a recompute of the request's `ordered` / `completedOn`, because
     * both are derived from the set of orders and neither is touched here. See deletePurchaseOrder()
     * in browse-purchasing for the pairing.
     */
    public deletePurchaseOrder(purchaseOrderId: string): Observable<void> {
        return from(this.appwrite.collection<PurchaseOrder>(Collection.purchaseOrder).doc(purchaseOrderId).delete());
    }

    public setPurchaseOrder(purchaseOrder: PurchaseOrder): Observable<void> {
        return from(this.appwrite.collection<PurchaseOrder>(Collection.purchaseOrder).doc(purchaseOrder.id).set(purchaseOrder));
    }

    public setPurchaseOrders(purchaseOrders: PurchaseOrder[]): Observable<void> {
        return forkJoin(purchaseOrders.map((purchaseOrder) => this.setPurchaseOrder(purchaseOrder))).pipe(
            defaultIfEmpty([]),
            map(() => void 0),
        );
    }

    public setPurchaseOrdersAndPurchaseRequests(purchaseOrders: PurchaseOrder[], purchaseRequests: PurchaseRequest[]): Observable<void> {
        const groups: BatchSetEntityGroups = {
            [Collection.purchaseOrder]: purchaseOrders,
            [Collection.purchaseRequest]: purchaseRequests,
        }

        return this.appwrite.batchSetAcrossCollections(groups)
    }

    public getPurchasingPageValueChanges(): Observable<PurchaseRequest[]> {
        const fiveDaysAgo = new Date()
        fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5)
        return this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).valueChanges().pipe(
            map((items) => items.filter((item) => !!item.readiedOn)),
            map((items) => items.filter((item) => {
                if (!!item.completedOn && (item.completedOn.getTime() - fiveDaysAgo.getTime() < 0)) return false
                return true
            }))
        );
    }

    public getPurchaseRequestsValueChanges(): Observable<PurchaseRequest[]> {
        const fiveDaysAgo = new Date()
        fiveDaysAgo.setDate(fiveDaysAgo.getDate() - 5)
        return this.appwrite.collection<PurchaseRequest>(Collection.purchaseRequest).valueChanges().pipe(
            map((items) => items.filter((item) => {
                if (!!item.completedOn && (item.completedOn.getTime() - fiveDaysAgo.getTime() < 0)) return false
                return true
            }))
        );
    }

    public loadOnlyReadiedPurchaseRequests(): Observable<PurchaseRequest[]> {
        return this.appwrite.list<PurchaseRequest>(Collection.purchaseRequest, [Query.limit(5000)]).pipe(
            map((resp) => resp.items.filter((request) => !!request.readiedOn && !request.ordered && !request.completedOn))
        );
    }

    public getPurchaseRequestsByPurchaseRequestListIdValueChanges(listId: string): Observable<PurchaseRequest[]> {
        return this.appwrite.collection<PurchaseRequest>(
            Collection.purchaseRequest,
            ref => ref.where('purchaseRequestListId', '==', listId)
        ).valueChanges().pipe(defaultIfEmpty([]));
    }

    public loadReceivedPurchaseOrdersBetween(startDate: Date, endDate: Date) {
        return this.appwrite.list<PurchaseOrder>(Collection.purchaseOrder, [
            Query.greaterThanEqual('receivedOn', startDate.toISOString()),
            Query.lessThanEqual('receivedOn', endDate.toISOString()),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    // Query.contains works against native string-array fields when an index
    // is present. Originals preserved as a /* */ block at the bottom of the
    // file for revert.
    public loadContacts(): Observable<Contact[]> {
        return this.appwrite.list<Contact>(Collection.contact, [Query.limit(1000)]).pipe(
            map(resp => resp.items),
        );
    }

    public loadAssociatedContactsByVendorId(vendorId: string): Observable<Contact[]> {
        // Appwrite doesn't allow indexes on array attributes,
        // so server-side array-contains queries don't work. Load all contacts
        // and filter client-side. Fine at 223 contacts; revisit if it scales
        // (split into a contact-vendor mapping collection at that point).
        return this.appwrite.list<Contact>(Collection.contact, [Query.limit(1000)]).pipe(
            map(resp => resp.items.filter(c => c.associatedVendorIds?.includes(vendorId))),
        );
    }

    public setContact(contact: Contact): Observable<void> {
        return from(this.appwrite.collection<Contact>(Collection.contact).doc(contact.id).set(contact));
    }

    public loadContactById(contactId: string): Observable<Contact> {
        return this.appwrite.get<Contact>(Collection.contact, contactId);
    }

    public loadContactsByIds(contactIds: string[]): Observable<Contact[]> {
        return this.appwrite.listByIds<Contact>(Collection.contact, contactIds);
    }

    public loadContactsByEmails(emails: string[]): Observable<Contact[]> {
        const uniqueEmails = [...new Set(emails.filter(email => !!email))];
        if (uniqueEmails.length === 0) return of([]);

        const batches: string[][] = [];
        for (let i = 0; i < uniqueEmails.length; i += 30) {
            batches.push(uniqueEmails.slice(i, i + 30));
        }

        return forkJoin(batches.map(batch => this.appwrite.list<Contact>(Collection.contact, [
            Query.contains('emails', batch),
            Query.limit(1000),
        ]).pipe(
            map(resp => resp.items),
        ))).pipe(
            defaultIfEmpty([] as Contact[][]),
            map(groups => [...new Map(groups.flat().map(contact => [contact.id, contact])).values()]),
        );
    }

    public loadContactAssociationByCustomerId(customerId: string): Observable<Contact[]> {
        // same array-attribute index limitation; load + filter.
        return this.appwrite.list<Contact>(Collection.contact, [Query.limit(1000)]).pipe(
            map(resp => resp.items.filter(c => c.associatedCustomerIds?.includes(customerId))),
        );
    }

    public loadSoftwareRequests(): Observable<SoftwareRequest[]> {
        return this.appwrite.list<SoftwareRequest>(Collection.softwareRequest, [Query.limit(1000)]).pipe(
            map(resp => resp.items)
        );
    }

    public loadSoftwareRequestById(requestId: string): Observable<SoftwareRequest> {
        return this.appwrite.get<SoftwareRequest>(Collection.softwareRequest, requestId);
    }
    public loadSoftwareRequestByTaskId(taskId: string): Observable<SoftwareRequest> {
        return this.appwrite.list<SoftwareRequest>(Collection.softwareRequest, [
            Query.equal('softwareTaskId', taskId),
            Query.limit(1),
        ]).pipe(
            map((resp) => resp.items[0]),
        );
    }

    public setSoftwareRequest(softwareRequest: SoftwareRequest): Observable<void> {
        return from(this.appwrite.collection<SoftwareRequest>(Collection.softwareRequest).doc(softwareRequest.id).set(softwareRequest));
    }

    public loadCommonProjects(): Observable<CommonProject[]> {
        return this.appwrite.list<CommonProject>(Collection.commonProject, [
            Query.orderDesc('updatedOn'),
            Query.limit(5000),
        ]).pipe(
            map(response => response.items),
        );
    }

    public loadCommonProjectById(projectId: string): Observable<CommonProject> {
        return this.appwrite.get<CommonProject>(Collection.commonProject, projectId);
    }

    public loadCommonProjectUpdates(projectId: string): Observable<CommonProjectUpdate[]> {
        return this.appwrite.list<CommonProjectUpdate>(Collection.commonProjectUpdate, [
            Query.equal('projectId', projectId),
            Query.orderDesc('timestamp'),
            Query.limit(5000),
        ]).pipe(
            map(response => response.items),
        );
    }

    public setCommonProject(project: CommonProject): Observable<void> {
        return from(this.appwrite.collection<CommonProject>(Collection.commonProject).doc(project.id).set(project));
    }

    public appendCommonProjectUpdate(
        project: CommonProject,
        update: CommonProjectUpdate,
    ): Observable<CommonProject> {
        const updatedProject: CommonProject = {
            ...project,
            updatedOn: update.timestamp,
            updatedById: update.createdById,
        };

        return this.appwrite.batchSetAcrossCollectionsTransactional({
            [Collection.commonProject]: [updatedProject],
            [Collection.commonProjectUpdate]: [update],
        }).pipe(
            map(() => updatedProject),
        );
    }

    public loadSmartJeffProjects(): Observable<SmartJeffProject[]> {
        return this.appwrite.list<SmartJeffProject>(Collection.smartJeffProject, [Query.limit(1000)]).pipe(
            map(resp => resp.items)
        );
    }

    public loadSmartJeffProjectById(projectId: string): Observable<SmartJeffProject> {
        return this.appwrite.get<SmartJeffProject>(Collection.smartJeffProject, projectId);
    }

    public setSmartJeffProject(project: SmartJeffProject): Observable<void> {
        return from(this.appwrite.collection<SmartJeffProject>(Collection.smartJeffProject).doc(project.id).set(project));
    }

    public deleteSmartJeffProject(projectId: string): Observable<void> {
        return this.appwrite.delete(Collection.smartJeffProject, projectId);
    }

    public loadMaintenanceTasks(): Observable<MaintenanceTask[]> {
        return this.appwrite.list<MaintenanceTask>(Collection.maintenanceTask, [Query.limit(1000)]).pipe(
            map(resp => resp.items)
        );
    }
    public loadMaintenanceTaskById(taskId: string): Observable<MaintenanceTask> {
        return this.appwrite.get<MaintenanceTask>(Collection.maintenanceTask, taskId);
    }
    public setMaintenanceTask(task: MaintenanceTask): Observable<void> {
        return from(this.appwrite.collection<MaintenanceTask>(Collection.maintenanceTask).doc(task.id).set(task));
    }

    public setProcedureCamData(values: ProcedureCamEntry[]): Observable<void> {
        console.log('setProcedureCamData', values)
        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.ProcedureCAMData).set({ values }));
    }

    public deleteUserEntryForStepFromProcedureCamData(userId: string, stepId: string): Observable<void> {
        return this.loadProcedureCamData().pipe(
            switchMap(resp => {
                const updated = resp.filter(entry => entry.userId !== userId || entry.stepId !== stepId);
                console.log('deleteUserEntryForStepFromProcedureCamData', updated)
                return this.setProcedureCamData(updated);
            })
        )
    }
    public deleteUserEntriesForProcedureVersionFromProcedureCamData(userId: string, procedureVersionId: string): Observable<void> {
        return this.loadProcedureCamData().pipe(
            switchMap(resp => {
                const updated = resp.filter(entry => entry.userId !== userId || entry.procedureVersionId !== procedureVersionId);
                console.log('deleteUserEntriesForProcedureVersionFromProcedureCamData', updated)
                return this.setProcedureCamData(updated);
            })
        )
    }

    public getProcedureCamDataValueChanges(): Observable<ProcedureCamEntry[]> {
        const doc = this.appwrite.collection<{ values: ProcedureCamEntry[] }>(Collection.globalVariables).doc(GlobalVariableDocumentIds.ProcedureCAMData);
        return doc.valueChanges().pipe(
            catchError((error) => {
                if (!this.isAppwriteNotFound(error)) {
                    return throwError(() => error);
                }
                return this.setProcedureCamData([]).pipe(
                    switchMap(() => doc.valueChanges()),
                );
            }),
            map((resp) => {
                if (!resp || !resp.values) return []
                return resp.values
            }),
        )
    }

    public loadProcedureCamData(): Observable<ProcedureCamEntry[]> {
        return this.appwrite.get<{ values: ProcedureCamEntry[] }>(Collection.globalVariables, GlobalVariableDocumentIds.ProcedureCAMData).pipe(
            map(resp => {
                if (!resp || !resp.values) return []

                return resp.values
            }),
            catchError((error) => {
                if (!this.isAppwriteNotFound(error)) {
                    return throwError(() => error);
                }
                return of([]);
            }),
        )
    }

    private isAppwriteNotFound(error: unknown): boolean {
        return typeof error === 'object'
            && error !== null
            && 'code' in error
            && (error as { code?: number }).code === 404;
    }

    public loadPurchaseRequestsByHardwareItemId(hardwareId: string): Observable<PurchaseRequest[]> {
        return this.appwrite.list<PurchaseRequest>(Collection.purchaseRequest, [
            Query.equal('hardwareId', hardwareId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadPurchaseOrdersByPurchaseRequestId(requestId: string): Observable<PurchaseOrder[]> {
        return this.appwrite.list<PurchaseOrder>(Collection.purchaseOrder, [
            Query.equal('purchaseRequestId', requestId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadPurchaseOrdersByPurchaseRequestIds(requestIds: string[]): Observable<PurchaseOrder[]> {
        return this.appwrite.listByFieldValues<PurchaseOrder>(Collection.purchaseOrder, 'purchaseRequestId', requestIds);
    }

    public loadActivePurchaseOrdersByPurchaseRequestId(requestId: string): Observable<PurchaseOrder[]> {
        return this.appwrite.list<PurchaseOrder>(Collection.purchaseOrder, [
            Query.equal('purchaseRequestId', requestId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items.filter((order) => !!order.orderedOn && !order.receivedOn))
        );

    }

    public loadActivePurchaseOrdersByPurchaseRequestIds(requestIds: string[]): Observable<PurchaseOrder[]> {
        return this.loadPurchaseOrdersByPurchaseRequestIds(requestIds).pipe(
            map((orders) => orders.filter((order) => !!order.orderedOn && !order.receivedOn)),
        );
    }

    public loadOrderedPurchaseRequestsByHardwareId(hardwareId: string): Observable<PurchaseRequest[]> {
        return this.appwrite.list<PurchaseRequest>(Collection.purchaseRequest, [
            Query.equal('hardwareId', hardwareId),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items.filter((request) => request.ordered))
        );
    }

    public loadCustomerByName(name: string): Observable<Customer> {
        return this.appwrite.list<Customer>(Collection.customer, [Query.equal('name', name), Query.limit(1)]).pipe(
            map(resp => resp.items[0]),
        );
    }

    public loadPlasticsScrapAssetId(): Observable<string> {
        return this.appwrite.collection<{ assetId: string }>(Collection.globalVariables)
            .doc(GlobalVariableDocumentIds.PlasticsScrapAssetId)
            .get()
            .pipe(
                map(resp => resp.data()?.assetId || '')
            );
    }

    public setPlasticsScrapAssetId(assetId: string): Observable<void> {
        return from(this.appwrite.collection<any>(Collection.globalVariables).doc(GlobalVariableDocumentIds.PlasticsScrapAssetId).set({ assetId }));
    }

    public loadEmailGroups(): Observable<EmailGroup[]> {
        return this.functions.get<EmailGroup[]>('api', `/${AppwriteApiRoute.Utility}/${AppwriteApiEndpoint.LoadEmailGroups}`).pipe(
            catchError(err => {
                console.log(err);
                return of([]);
            })
        )
    }

    public getDirectChatsByUserIdValueChanges(userId: string): Observable<DirectChat[]> {
        return this.appwrite.collection<DirectChat>(Collection.directChat).valueChanges().pipe(
            map((chats) => chats
                .filter((chat) => chat.participantIds?.includes(userId))
                .sort((a, b) => b.updatedOn.getTime() - a.updatedOn.getTime())
            )
        );
    }

    public getDirectChatByIdValueChanges(chatId: string): Observable<DirectChat> {
        return this.appwrite.collection<DirectChat>(Collection.directChat).doc(chatId).valueChanges().pipe(
            map(chat => chat ?? null as unknown as DirectChat)
        );
    }

    public loadDirectChatByParticipantIdKey(key: string): Observable<DirectChat | null> {
        return this.appwrite.list<DirectChat>(Collection.directChat, [
            Query.equal('participantIdKey', key),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.items.length > 0 ? resp.items[0] : null)
        );
    }

    public getDirectChatMessagesByChatIdValueChanges(chatId: string): Observable<DirectChatMessage[]> {
        return this.appwrite.collection<DirectChatMessage>(Collection.directChatMessage, ref => ref
            .where('chatId', '==', chatId)
            .orderBy('createdOn', 'asc')
        ).valueChanges();
    }

    public setDirectChatMessage(message: DirectChatMessage, updatedChat: DirectChat): Observable<void> {
        return this.appwrite.batchSetAcrossCollections({
            [Collection.directChatMessage]: [message],
            [Collection.directChat]: [updatedChat],
        });
    }

    public setDirectChat(chat: DirectChat): Observable<void> {
        return from(this.appwrite.collection<DirectChat>(Collection.directChat).doc(chat.id).set(chat));
    }

    public setDirectChatTypingUser(chatId: string, userId: string): Observable<void> {
        // TODO: Direct chat is not production-ready; rework as Appwrite read-modify-write.
        void chatId;
        void userId;
        return of(undefined);
    }

    public removeDirectChatTypingUser(chatId: string, userId: string): Observable<void> {
        // TODO: Direct chat is not production-ready; rework as Appwrite read-modify-write.
        void chatId;
        void userId;
        return of(undefined);
    }

    public muteDirectChat(chatId: string, userId: string): Observable<void> {
        // TODO: Direct chat is not production-ready; rework as Appwrite read-modify-write.
        void chatId;
        void userId;
        return of(undefined);
    }

    public unmuteDirectChat(chatId: string, userId: string): Observable<void> {
        // TODO: Direct chat is not production-ready; rework as Appwrite read-modify-write.
        void chatId;
        void userId;
        return of(undefined);
    }

    public loadFinanceEntriesByType(type: FinanceEntryType): Observable<FinanceEntry[]> {
        return this.appwrite.list<FinanceEntry>(Collection.financeEntry, [
            Query.equal('type', type),
            Query.orderDesc('createdOn'),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items)
        );
    }

    public loadFinanceEntriesByTypePaginated(
        type: FinanceEntryType,
        pageSize: number,
        startAfterDoc?: string | null,
    ): Observable<{ entries: FinanceEntry[]; lastDoc: string | null }> {
        void startAfterDoc;
        return this.appwrite.list<FinanceEntry>(Collection.financeEntry, [
            Query.equal('type', type),
            Query.orderDesc('createdOn'),
            Query.limit(pageSize),
        ]).pipe(
            map((resp) => ({
                entries: resp.items,
                lastDoc: null,
            }))
        );
    }

    public loadFinanceEntries(type: FinanceEntryType, since: Date): Observable<FinanceEntry[]> {
        return this.appwrite.list<FinanceEntry>(Collection.financeEntry, [
            Query.equal('type', type),
            Query.orderDesc('createdOn'),
            Query.limit(500),
        ]).pipe(
            map((resp) => resp.items
                .filter(e => new Date(e.createdOn).getTime() >= since.getTime())
                .reverse()
            )
        );
    }

    public setFinanceEntry(entry: FinanceEntry): Observable<void> {
        return from(this.appwrite.collection<FinanceEntry>(Collection.financeEntry).doc(entry.id).set(entry));
    }

    public loadOvertimeSummaryWeeks(): Observable<OvertimeSummaryWeek[]> {
        return this.appwrite.list<OvertimeSummaryWeek>(Collection.overtimeSummaryWeek, [
            Query.orderAsc('weekOf'),
            Query.limit(1000),
        ]).pipe(
            map((resp) => resp.items.map(normalizeOvertimeWeek))
        );
    }

    public setOvertimeSummaryWeek(week: OvertimeSummaryWeek): Observable<void> {
        return from(this.appwrite.collection<OvertimeSummaryWeek>(Collection.overtimeSummaryWeek).doc(week.id).set(week));
    }

    public deleteOvertimeSummaryWeek(id: string): Observable<void> {
        return from(this.appwrite.collection<OvertimeSummaryWeek>(Collection.overtimeSummaryWeek).doc(id).delete());
    }

    public setVacationEvent(event: VacationEvent): Observable<void> {
        return from(this.appwrite.collection<VacationEvent>(Collection.vacationEvent).doc(event.id).set(event));
    }

    public setVacationEvents(events: VacationEvent[]): Observable<void> {
        if (events.length === 0) {
            return of(void 0);
        }

        return forkJoin(events.map(event => this.setVacationEvent(event))).pipe(
            map(() => void 0),
        );
    }

    public deleteVacationEvent(eventId: string): Observable<void> {
        return this.appwrite.delete(Collection.vacationEvent, eventId);
    }

    // --- HR employees ----------------------------------------------------------

    public loadEmployees(): Observable<Employee[]> {
        return this.appwrite.collection<Employee>(
            Collection.employee,
            ref => ref.orderBy('lastName', 'asc').limit(5000),
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs)),
        );
    }

    /** Just the named employees, for screens that only need a few (e.g. who is on vacation). */
    public loadEmployeesByIds(employeeIds: string[]): Observable<Employee[]> {
        if (!employeeIds.length) {
            return of([]);
        }
        return this.appwrite.list<Employee>(Collection.employee, [
            Query.equal('$id', employeeIds),
            Query.limit(employeeIds.length),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public loadEmployeeById(employeeId: string): Observable<Employee | null> {
        return this.appwrite.get<Employee>(Collection.employee, employeeId).pipe(
            catchError(() => of(null)),
        );
    }

    public setEmployee(employee: Employee): Observable<void> {
        return this.appwrite.set(Collection.employee, employee.id, employee);
    }

    /**
     * Saves the employee, then writes the records that document whatever changed (hire, title,
     * department, manager, status). `before` is null for a brand-new employee.
     */
    public saveEmployeeWithChangeRecords(
        before: Employee | null,
        after: Employee,
        context: Omit<EmployeeChangeContext, 'createId'>,
    ): Observable<EmployeeRecord[]> {
        const records = employeeChangeRecords(before, after, { ...context, createId: () => this.appwrite.createId() });
        return this.setEmployee(after).pipe(
            switchMap(() => records.length
                ? forkJoin(records.map(record => this.setEmployeeRecord(record)))
                : of([])),
            map(() => records),
        );
    }

    public loadEmployeeRecords(employeeId: string): Observable<EmployeeRecord[]> {
        return this.appwrite.collection<EmployeeRecord>(
            Collection.employeeRecord,
            ref => ref.where('employeeId', '==', employeeId).orderBy('effectiveDate', 'desc').limit(5000),
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs)),
        );
    }

    /** Training / certification records whose expiration is on or before `horizon`. */
    public loadExpiringEmployeeRecords(horizon: Date): Observable<EmployeeRecord[]> {
        return this.appwrite.list<EmployeeRecord>(Collection.employeeRecord, [
            Query.lessThanEqual('expirationDate', horizon.toISOString()),
            Query.orderAsc('expirationDate'),
            Query.limit(5000),
        ]).pipe(
            map(resp => resp.items),
        );
    }

    public setEmployeeRecord(record: EmployeeRecord): Observable<void> {
        return this.appwrite.set(Collection.employeeRecord, record.id, record);
    }

    public deleteEmployeeRecord(recordId: string): Observable<void> {
        return this.appwrite.delete(Collection.employeeRecord, recordId);
    }
    public loadVacationEventsBetween(startDate: Date, endDate: Date): Observable<VacationEvent[]> {
        return this.appwrite.collection<VacationEvent>(
            Collection.vacationEvent,
            ref => ref
                .where('startDate', '<=', endDate.toISOString())
                .where('endDate', '>=', startDate.toISOString())
                .orderBy('startDate', 'asc')
                .limit(5000),
        ).get().pipe(
            map(resp => fromAppwriteDocs(resp.docs))
        );
    }

    public loadVacationEventCountBetween(startDate: Date, endDate: Date): Observable<number> {
        return this.appwrite.list<VacationEvent>(Collection.vacationEvent, [
            Query.lessThanEqual('startDate', endDate.toISOString()),
            Query.greaterThanEqual('endDate', startDate.toISOString()),
            Query.limit(1),
        ]).pipe(
            map(resp => resp.total),
        );
    }
}

/** A UPS shipment's ship-to / ship-from parties rebuilt from the documents it references. */
export interface ResolvedUpsParties {
    shipTo: UpsShipmentParty;
    shipFrom: UpsShipmentParty;
}
